// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Account from "./account.js"
import AccountUserAudit from "./account-user-audit.js"
import User from "./user.js"

const modelClassData = {
  "attributes": {
    "account_id": {
      "column": {
        "default": null,
        "name": "account_id",
        "null": false,
        "type": null
      },
      "name": "account_id",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "role": {
      "column": {
        "default": null,
        "name": "role",
        "null": false,
        "type": "string"
      },
      "name": "role",
      "selected_by_default": null,
      "translated": null
    },
    "translated_role": {
      "column": null,
      "name": "translated_role",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "user_id": {
      "column": {
        "default": null,
        "name": "user_id",
        "null": false,
        "type": null
      },
      "name": "user_id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "AccountUser",
  "collectionKey": "account_users",
  "collectionName": "account_users",
  "i18nKey": "account_user",
  "camelizedLower": "accountUser",
  "name": "AccountUser",
  "nameDasherized": "account-user",
  "pluralName": "account_users",
  "ransackable_associations": [
    {
      "className": "Account",
      "collectionName": "accounts",
      "foreignKey": "account_id",
      "name": "account",
      "macro": "belongs_to",
      "resource_name": "Account",
      "through": null
    },
    {
      "className": "AccountUser::Audit",
      "collectionName": "account_user_audits",
      "foreignKey": "account_user_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "AccountUserAudit",
      "through": null
    },
    {
      "className": "AccountUser::Audit",
      "collectionName": "account_user_audits",
      "foreignKey": "account_user_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "AccountUserAudit",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "account_id",
      "column": {
        "default": null,
        "name": "account_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "role",
      "column": {
        "default": null,
        "name": "role",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "user_id",
      "column": {
        "default": null,
        "name": "user_id",
        "null": false,
        "type": null
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Account",
      "collectionName": "accounts",
      "foreignKey": "account_id",
      "name": "account",
      "macro": "belongs_to",
      "resource_name": "Account",
      "through": null
    },
    {
      "className": "AccountUser::Audit",
      "collectionName": "account_user_audits",
      "foreignKey": "account_user_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "AccountUserAudit",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "paramKey": "account_user",
  "primaryKey": "id"
}

/** Frontend model for AccountUser. */
export default class AccountUser extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  accountId() {
    return this.readAttributeUnderscore("account_id")
  }

  /** @returns {boolean} */
  hasAccountId() {
    const value = this.accountId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  role() {
    return this.readAttributeUnderscore("role")
  }

  /** @returns {boolean} */
  hasRole() {
    const value = this.role()

    return this._isPresent(value)
  }

  /** @returns {any} */
  translatedRole() {
    return this.readAttributeUnderscore("translated_role")
  }

  /** @returns {boolean} */
  hasTranslatedRole() {
    const value = this.translatedRole()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  userId() {
    return this.readAttributeUnderscore("user_id")
  }

  /** @returns {boolean} */
  hasUserId() {
    const value = this.userId()

    return this._isPresent(value)
  }

  /** @returns {import("./account.js").default | null} */
  account() {
    return this._readBelongsToReflection({modelClass: Account, reflectionName: "account"})
  }

  /** @returns {Promise<import("./account.js").default | null>} */
  loadAccount() {
    if (!("accountId" in this)) throw new Error("Foreign key method wasn't defined: accountId")

    const id = this.accountId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "account",
        model: this,
        modelClass: Account
      },
      {ransack}
    )
  }

  /** @returns {import("./account-user-audit.js").default | null} */
  createAudit() {
    return this._readHasOneReflection({modelClass: AccountUserAudit, reflectionName: "create_audit"})
  }

  /** @returns {Promise<import("./account-user-audit.js").default | null>} */
  loadCreateAudit() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()
    const ransack = {}

    ransack["account_user_id_eq"] = id

    return this._loadHasOneReflection(
      {
        reflectionName: "create_audit",
        model: this,
        modelClass: AccountUserAudit
      },
      {ransack}
    )
  }

  /** @returns {import("./user.js").default | null} */
  user() {
    return this._readBelongsToReflection({modelClass: User, reflectionName: "user"})
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  loadUser() {
    if (!("userId" in this)) throw new Error("Foreign key method wasn't defined: userId")

    const id = this.userId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "user",
        model: this,
        modelClass: User
      },
      {ransack}
    )
  }
}
